/**
 * What's new (/changelog). The read-only changelog every signed-in user sees,
 * reached from the avatar menu in the header.
 *
 * Not a catalog tool, for the same reason /settings is not: it belongs to the
 * signed-in person rather than to a department, and adding it to
 * COCKPIT_DEPARTMENTS would trip the Access-console parity test.
 *
 * Entries are authored at /it/changelog (IT, `it.changelog`). This page shows
 * the Cockpit feed, which includes entries targeting `both`. Per Steven
 * (2026-08-10) it uses the shared responsive grid: a table on a desktop and
 * cards on a phone, via the shell's `renderCards`.
 */

import { useEffect } from 'react';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { gridTheadClassName, useIsMobile } from '@ampersandbrands/ui';
import { Pill } from '@/components/Pill';
import { formatDate } from '@/lib/dateUtils';
import { withApiImageSrc } from '@/lib/richTextHtml';
import {
  changelogToolKey,
  newestPublishedAt,
  useChangelog,
  writeChangelogSeenAt,
  type ChangelogEntry,
} from '@/lib/useChangelog';

/**
 * The entry body. The API sanitizes `body_html` on write with the shared
 * allowlist in `@api/richtext` (scripts, event handlers, `javascript:` URLs and
 * off-site image sources are all dropped), which is what makes
 * dangerouslySetInnerHTML defensible here.
 *
 * `amp-rich-text` is the same class the editor surface uses, so an author sees
 * exactly what a reader gets.
 */
function EntryBody({ html }: { html: string }) {
  return (
    <div
      className="amp-rich-text max-w-none text-amp-ink dark:text-amp-dark-ink"
      dangerouslySetInnerHTML={{ __html: withApiImageSrc(html) }}
    />
  );
}

function ToolTags({ entry }: { entry: ChangelogEntry }) {
  if (entry.all_tools) {
    return <Pill variant="outline">All tools</Pill>;
  }
  if (entry.tools.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {entry.tools.map((t) => (
        <Pill key={changelogToolKey(t)} variant="outline">
          {t.tool_label}
        </Pill>
      ))}
    </div>
  );
}

export function ChangelogPage() {
  const isMobile = useIsMobile();
  const { data, isLoading, error } = useChangelog('cockpit');
  const entries = data?.entries ?? [];

  // Opening the page counts as reading everything published so far.
  const newest = newestPublishedAt(data?.entries);
  useEffect(() => {
    if (newest) writeChangelogSeenAt(newest);
  }, [newest]);

  // One empty-state message for all three quiet cases, so a reader is never
  // shown a stack trace or an error tone for "nothing published yet".
  const emptyMessage = isLoading
    ? 'Loading...'
    : error
      ? 'What’s new is not available right now. Try again shortly.'
      : 'Nothing has been published yet. Changes will show up here.';

  const renderCards = () => (
    <div className="space-y-3 py-2">
      {entries.length === 0 ? (
        <p className="px-1 py-4 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          {emptyMessage}
        </p>
      ) : (
        entries.map((e) => (
          <article
            key={e.id}
            className="rounded-lg border border-amp-line bg-amp-white p-3 dark:border-amp-dark-line dark:bg-amp-dark-surface"
          >
            <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark">
              {formatDate(e.published_at)}
            </p>
            <h2 className="mt-0.5 text-[16px] font-semibold text-amp-ink dark:text-amp-dark-ink">
              {e.title}
            </h2>
            <div className="mt-2">
              <EntryBody html={e.body_html} />
            </div>
            <div className="mt-2">
              <ToolTags entry={e} />
            </div>
          </article>
        ))
      )}
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-6">
        <p className="mb-1 text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark">
          Cockpit
        </p>
        <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">
          What&apos;s new
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-amp-ink-muted line-clamp-2 dark:text-amp-dark-ink-muted md:line-clamp-none">
          Recent changes to Cockpit, newest first.
        </p>
      </div>

      <DataGridShell
        total={entries.length}
        loaded={entries.length}
        countNoun="updates"
        renderCards={isMobile ? renderCards : undefined}
      >
        <table className="min-w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              <th className="w-[130px] px-3 py-2 text-left font-semibold">Date</th>
              <th className="w-[280px] px-3 py-2 text-left font-semibold">Title</th>
              <th className="px-3 py-2 text-left font-semibold">What changed</th>
              <th className="w-[220px] px-3 py-2 text-left font-semibold">Tools</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {entries.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              entries.map((e) => (
                <tr key={e.id}>
                  <td className="px-3 py-3 align-top text-amp-ink-muted dark:text-amp-dark-ink-muted">
                    {formatDate(e.published_at)}
                  </td>
                  <td className="px-3 py-3 align-top font-semibold text-amp-ink dark:text-amp-dark-ink">
                    {e.title}
                  </td>
                  <td className="px-3 py-3 align-top">
                    <EntryBody html={e.body_html} />
                  </td>
                  <td className="px-3 py-3 align-top">
                    <ToolTags entry={e} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DataGridShell>
    </div>
  );
}
